import { readFileSync } from "node:fs";
import { relative, basename } from "node:path";
import { findScaffoldFiles } from "./drift/index.js";
import { estimateTokens, stripFrontmatter } from "./utils/fs.js";
import { buildSectionIndex, buildEdgeMap } from "./mcp/section-index.js";
import { routeContext } from "./mcp/context-router.js";

/**
 * Honest, deterministic token-cost benchmark — no model calls.
 *
 * Compares the per-turn standing context cost of two strategies on the SAME
 * scaffold, for a set of representative tasks:
 *
 *   Arm A (flat):  the naive "dump everything into one CLAUDE.md" model —
 *                  every scaffold file is in context on every turn.
 *   Arm B (CAI):   the always-loaded anchor (AGENTS.md / ROUTER.md) plus only
 *                  the task-relevant context the router actually serves.
 *
 * Arm B uses CAI's real routeContext pipeline (findScaffoldFiles ->
 * buildSectionIndex -> routeContext), so the number reflects what CAI would
 * actually feed an agent, not an estimate of a strawman.
 *
 * The result reports the median saving AND the always-loaded anchor tax that
 * Arm B pays every turn, plus the per-task breakdown — the standing cost most
 * "90% saved" claims hide.
 */

/** Files that CAI loads on every turn regardless of task. */
const ANCHOR_BASENAMES = ["AGENTS.md", "ROUTER.md", "CLAUDE.md"];

export interface BenchTaskResult {
  task: string;
  flatTokens: number;
  caiTokens: number;
  savedTokens: number;
  savedPct: number;
}

export interface BenchResult {
  scaffoldFiles: number;
  flatTotalTokens: number;
  anchorTokens: number;
  budget: number;
  tasks: BenchTaskResult[];
  medianSavedPct: number;
  /** Tasks where CAI cost MORE than flat (negative saving). */
  losses: number;
  /**
   * Tasks where the router served real context beyond the anchor. When this is
   * low, the high "saving" is an artifact of the router matching nothing (bad
   * scaffold or unrepresentative tasks), NOT genuine savings — read with care.
   */
  routedTaskCount: number;
}

export interface BenchOptions {
  /** Router budget per task (tokens). Defaults to the router's own default. */
  budget?: number;
  /** Tasks to route on. Defaults to a small generic suite. */
  tasks?: string[];
}

const DEFAULT_TASKS = [
  "add a new API endpoint",
  "fix a failing test",
  "explain how the build works",
  "update a dependency",
  "add a new database model",
];

function readStripped(absPath: string): string {
  try {
    return stripFrontmatter(readFileSync(absPath, "utf-8"));
  } catch {
    return "";
  }
}

function isAnchor(absPath: string): boolean {
  return ANCHOR_BASENAMES.includes(basename(absPath));
}

export function runBench(
  projectRoot: string,
  scaffoldRoot: string,
  opts: BenchOptions = {}
): BenchResult {
  const tasks = opts.tasks ?? DEFAULT_TASKS;
  const scaffoldFiles = findScaffoldFiles(projectRoot, scaffoldRoot);

  // Arm A: flat — every scaffold file is in context, every turn.
  const flatTotalTokens = scaffoldFiles.reduce(
    (sum, f) => sum + estimateTokens(readStripped(f)),
    0
  );

  // Anchor tax: files CAI always loads.
  const anchorTokens = scaffoldFiles
    .filter(isAnchor)
    .reduce((sum, f) => sum + estimateTokens(readStripped(f)), 0);

  // Build the real router index once.
  const index = buildSectionIndex(scaffoldFiles, projectRoot, readStripped);
  const edgeMap = buildEdgeMap(scaffoldFiles, projectRoot, scaffoldRoot);
  const budget = opts.budget ?? 4000;

  let routedTaskCount = 0;
  const taskResults: BenchTaskResult[] = tasks.map((task) => {
    const routed = routeContext({ task, budget }, index, [], edgeMap);
    if (routed.total_tokens > 0) routedTaskCount++;
    // Arm B: anchor (always loaded) + what the router serves for this task.
    const caiTokens = anchorTokens + routed.total_tokens;
    const savedTokens = flatTotalTokens - caiTokens;
    const savedPct = flatTotalTokens > 0 ? (savedTokens / flatTotalTokens) * 100 : 0;
    return {
      task,
      flatTokens: flatTotalTokens,
      caiTokens,
      savedTokens,
      savedPct: Math.round(savedPct * 10) / 10,
    };
  });

  const pcts = taskResults.map((t) => t.savedPct).sort((a, b) => a - b);
  const medianSavedPct = pcts.length
    ? pcts[Math.floor((pcts.length - 1) / 2)]
    : 0;
  const losses = taskResults.filter((t) => t.savedTokens < 0).length;

  return {
    scaffoldFiles: scaffoldFiles.length,
    flatTotalTokens,
    anchorTokens,
    budget,
    tasks: taskResults,
    medianSavedPct,
    losses,
    routedTaskCount,
  };
}
